/**
 * Pure helpers of the one-time introduction to the "try the new version" pill (see UiSwitchCoachMark.tsx and SwitchUiHeaderItem.tsx).
 * Nothing here touches React, so it can be unit tested.
 */

/** localStorage key of the "already shown or dismissed" flag. Once per browser, the legacy UI and the new UI each have their own. */
export const COACH_STORAGE_KEY = "oper8.uiSwitch.coach.legacy";

/** Name of the data attribute set on `<html>` to draw the glow ring on the pill (switchUi.css); see {@link CoachAttention}. */
export const ATTENTION_ATTRIBUTE = "data-switch-ui-attention";

/** Below this width the pill uses its short label. Same value as the new UI's breakpoint. */
export const NARROW_QUERY = "(max-width: 1099.98px)";

/** `spot` while the coach-mark is open; `pulse` for a few seconds after it was dismissed (three pulses, static under reduced motion). */
export type CoachAttention = "spot" | "pulse";

/** The pulse lasts 3 iterations of the 2.2 s animation in switchUi.css; keep the two in step. */
export const PULSE_DURATION_MS = 3 * 2200 + 200;

type StorageLike = Pick<Storage, "getItem" | "setItem">;

/** localStorage can throw (blocked cookies, private mode, quota): an unavailable store means "unknown", never a failure. */
const getDefaultStorage = (): StorageLike | null => {
    try {
        return window.localStorage;
    } catch {
        return null;
    }
};

/** True once the coach-mark was shown (and dismissed) in this browser. When storage is unavailable it counts as seen: never nag on every page. */
export const hasSeenCoach = (storage: StorageLike | null = getDefaultStorage()): boolean => {
    if (!storage) return true;
    try {
        return storage.getItem(COACH_STORAGE_KEY) !== null;
    } catch {
        return true;
    }
};

/** Remembers that the coach-mark was dismissed (also by Esc, an outside click and the "Try" button). */
export const markCoachSeen = (storage: StorageLike | null = getDefaultStorage()): void => {
    if (!storage) return;
    try {
        storage.setItem(COACH_STORAGE_KEY, "1");
    } catch {
        // Best effort: the coach-mark may come back on the next page load.
    }
};

/** Translation keys of the pill label: the long one on wide screens, the short one below 1100px. Never an icon alone. */
export const getPillLabelKey = (narrow: boolean): "uiSwitch.tryNew" | "uiSwitch.tryNewShort" => (narrow ? "uiSwitch.tryNewShort" : "uiSwitch.tryNew");

export interface IAnchorRect {
    left: number;
    right: number;
    bottom: number;
}

export interface ICoachPosition {

    /** Viewport coordinates of the popover's top-left corner (it is `position: fixed`). */
    left: number;
    top: number;

    /** Distance of the arrow's left edge from the popover's left edge. */
    arrow: number;
}

const ARROW_SIZE = 16;

/**
 * Where to put the popover: under the pill, its right edge a little past the pill's right edge (the pill is at the far right of the header),
 * kept inside the viewport, with the arrow still pointing at the pill's centre.
 */
export const computeCoachPosition = (anchor: IAnchorRect, popoverWidth: number, viewportWidth: number, gap = 14, margin = 12): ICoachPosition => {
    const wanted = anchor.right + 8 - popoverWidth;
    const left = Math.max(margin, Math.min(wanted, viewportWidth - margin - popoverWidth));
    const centre = (anchor.left + anchor.right) / 2;
    const arrow = Math.max(12, Math.min(centre - left - ARROW_SIZE / 2, popoverWidth - 12 - ARROW_SIZE));
    return { left, top: anchor.bottom + gap, arrow };
};

/** Sets (or, with null, clears) the glow ring on the pill. A data attribute on `<html>` because crc owns the pill's own attributes and class. */
export const setAttention = (mode: CoachAttention | null): void => {
    if (mode) document.documentElement.setAttribute(ATTENTION_ATTRIBUTE, mode);
    else document.documentElement.removeAttribute(ATTENTION_ATTRIBUTE);
};

/** The pill as rendered by the crc header, or null while the header has not drawn it (sign-in, no header). */
export const findPill = (): HTMLElement | null => {
    const pill = document.querySelector<HTMLElement>("#mainHeader .menuItem.switchUiItem > .menuItemInner") ??
        document.querySelector<HTMLElement>("[data-test=\"menu-header-switch-ui\"]");
    if (!pill) return null;
    const rect = pill.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 ? pill : null;
};
